
import { User } from "@/types";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Copy } from "lucide-react";
import { EditChildForm } from "./EditChildForm";
import { useToast } from "@/hooks/use-toast";
import { db } from "@/lib/firebase";
import { query, collection, where, getDocs } from "firebase/firestore";

interface ChildHeaderProps {
  child: User;
  isParent: boolean;
  onUpdateChild: (childId: string, firstName: string, lastName: string) => Promise<void>;
}

export function ChildHeader({ child, isParent, onUpdateChild }: ChildHeaderProps) {
  const { toast } = useToast();

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  };

  const handleCopyInviteUrl = async () => {
    try {
      // Find the invitation code for this temp user
      const invitationsQuery = query(
        collection(db, "invitations"),
        where("tempUserId", "==", child.id)
      );
      
      const invitationDocs = await getDocs(invitationsQuery);
      
      if (invitationDocs.empty) {
        toast({
          title: "Error",
          description: "No invitation found for this user.",
          variant: "destructive",
        });
        return;
      }
      
      // Get the invitation code (which is the document ID)
      const invitationCode = invitationDocs.docs[0].id;
      const inviteUrl = `${window.location.origin}/invite?code=${invitationCode}`;
      
      await navigator.clipboard.writeText(inviteUrl);
      toast({
        title: "Success",
        description: "Invite URL copied to clipboard!",
      });
    } catch (error) {
      console.error("Error copying invite URL:", error);
      toast({
        title: "Error",
        description: "Failed to copy invite URL. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="md:col-span-2">
      <CardHeader className="flex flex-row items-center gap-4">
        <Avatar className="h-16 w-16">
          <AvatarImage src={child.image} alt={child.firstName} />
          <AvatarFallback className="text-lg">
            {getInitials(child.firstName, child.lastName)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <CardTitle className="text-2xl">
            {child.firstName} {child.lastName}
          </CardTitle>
          <p className="text-muted-foreground">{child.username}</p>
          {child.isTemporary && isParent && (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-sm text-amber-600 bg-amber-50 px-2 py-1 rounded">
                Invite pending
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyInviteUrl}
                className="flex items-center gap-2"
              >
                <Copy className="h-3 w-3" />
                Copy Invite URL
              </Button>
            </div>
          )}
        </div>
        {isParent && (
          <EditChildForm child={child} onUpdateChild={onUpdateChild} />
        )}
      </CardHeader>
    </Card>
  );
}
